import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../CSS/signup.css';
import { sanitize } from '../../utils/sanitize';
import { REGEX as PASSWORD_REGEX, POLICY as PASSWORD_POLICY } from '../../policies/passwordPolicy';
import PasswordField from '../shared/PasswordField';
import LegalAccordion from '../shared/LegalAccordion';
import { canonicalizeEmail } from '../../utils/emailCanonicalize';

const AffiliatesSignup = () => {
  const [email, setEmail]                     = useState('');
  const [password, setPassword]               = useState('');
  // ⬇️ Rôle à l’inscription: affiliatepending (file d’attente)
  const role                                  = 'affiliatepending';
  const [message, setMessage]                 = useState({ text: '', isSuccess: false });
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [acceptedCGU, setAcceptedCGU]         = useState(false);
  const navigate                              = useNavigate();

  const isPwdValid = PASSWORD_REGEX.test(password || '');

  // --- Base API robuste (CF direct vs Domaine) ---
  const RAW_API =
    (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) ||
    (process.env.NODE_ENV === 'production'
      ? window.location.origin
      : 'http://localhost:5099');

  const API_BASE = RAW_API.replace(/\/+$/, '');
  const isCFDirect = /cloudfunctions\.net\/[^/]+$/i.test(API_BASE);

  const SIGNUP_ENDPOINT = isCFDirect
    ? `${API_BASE}/auth/signup`
    : `${API_BASE}/api/auth/signup`;

  const CSRF_ENDPOINT = isCFDirect
    ? `${API_BASE}/csrf-token`
    : `${API_BASE}/api/csrf-token`;

  const getCookie = (name) => {
    const m = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([$?*|{}\]\\^])/g, '\\$1') + '=([^;]*)')
    );
    return m ? decodeURIComponent(m[1]) : null;
  };

  const parseJsonSafe = async (res) => {
    const ct = res.headers.get('content-type') || '';
    if (ct.toLowerCase().includes('application/json')) {
      try { return await res.json(); } catch { return {}; }
    }
    return {};
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSignupLoading) return;

    setIsSignupLoading(true);
    setMessage({ text: '', isSuccess: false });

    if (!isPwdValid) {
      setMessage({ text: PASSWORD_POLICY.message, isSuccess: false });
      setIsSignupLoading(false);
      return;
    }

    const sanitizedEmail = sanitize(email);
    const canonicalEmail = canonicalizeEmail(sanitizedEmail);
    const rawPassword = password; // ne pas sanitizer le MDP

    try {
      // CSRF (non bloquant)
      try { await fetch(CSRF_ENDPOINT, { credentials: 'include' }); } catch {}

      const xsrf = getCookie('XSRF-TOKEN');

      const response = await fetch(SIGNUP_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(xsrf ? { 'X-CSRF-Token': xsrf } : {})
        },
        body: JSON.stringify({
          email:    canonicalEmail,
          password: rawPassword,
          role:     role, // ⬅️ affiliatepending
        })
      });

      const data = await parseJsonSafe(response);

      if (!response.ok) {
        const errorMessage =
          data?.error ||
          (data?.errors && data.errors.map((err) => err.msg).join(', ')) ||
          `Erreur (${response.status}) lors de l’inscription`;
        throw new Error(errorMessage);
      }

      setMessage({ text: 'Inscription réussie ! ✅', isSuccess: true });
      try { sessionStorage.setItem('ecobill_cgu_pending', 'affiliatepending'); } catch {}
      setTimeout(() => {
        const encodedEmail = encodeURIComponent(canonicalEmail);
        navigate(`/logingeneral?email=${encodedEmail}`);
      }, 1500);

    } catch (err) {
      console.error('Erreur signup affiliates :', err);
      const displayedMessage = err?.message?.startsWith?.('Failed')
        ? 'API non joignable'
        : (err?.message || 'Erreur réseau');
      setMessage({ text: displayedMessage, isSuccess: false });
      setTimeout(() => setMessage({ text: '', isSuccess: false }), 4000);
    } finally {
      setIsSignupLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <h2>Inscription affilié</h2>
      <form onSubmit={handleSubmit}>
        <div>
          <label>Email :</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSignupLoading}
            autoComplete="email"
            inputMode="email"
            spellCheck={false}
          />
        </div>

        <div>
          <label>Mot de passe :</label>
          <PasswordField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSignupLoading}
            required
            autoComplete="new-password"
            pattern={PASSWORD_REGEX.source}
            title={PASSWORD_POLICY.message}
            aria-invalid={!!password && !isPwdValid}
            spellCheck={false}
          />
        </div>

        <LegalAccordion
          docId="cgu"
          checked={acceptedCGU}
          onChange={setAcceptedCGU}
        />
        <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--eb-muted)', margin: '-4px 0 12px' }}>
          En vous inscrivant, vous reconnaissez également avoir pris connaissance de notre{' '}
          <Link to="/legal/politique-confidentialite" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--eb-brand)', fontWeight: 600 }}>
            Politique de confidentialité
          </Link>.
        </p>

        <button
          type="submit"
          disabled={isSignupLoading || !isPwdValid || !acceptedCGU}
          style={{
            cursor:  isSignupLoading || !isPwdValid || !acceptedCGU ? 'not-allowed' : 'pointer',
            opacity: isSignupLoading || !isPwdValid || !acceptedCGU ? 0.55 : 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {isSignupLoading && <span className="eb-spinner eb-spinner--sm eb-spinner--light" aria-hidden="true" />}
          {isSignupLoading ? "Inscription…" : "S'inscrire"}
        </button>

        {message.text && (
          <div className={`eb-alert ${message.isSuccess ? 'eb-alert--success' : 'eb-alert--danger'}`} role="alert" style={{ marginTop: 16 }}>
            {message.text}
          </div>
        )}

        <p style={{ marginTop: 20, fontSize: 14, color: 'var(--eb-muted)', textAlign: 'center' }}>
          Déjà un compte ?{' '}
          <Link to="/logingeneral" className="eb-link">
            Se connecter
          </Link>
        </p>
      </form>
    </div>
  );
};

export default AffiliatesSignup;
